import { LabelValuePair, SimplePrimitive, Maybe, Nullable } from "@vixi/types";
import { ComponentProps, ComponentPropsWithoutRef, forwardRef } from "react";
import { cn } from "@/lib/utils";
import { cva, VariantProps } from "class-variance-authority";

type TabButtonPropsWithoutRef = ComponentPropsWithoutRef<"button"> &
  VariantProps<typeof tabButtonVariants>;

const tabButtonVariants = cva(
  "rounded p-1.5 leading-none min-w-25 text-primary fs-15 capitalize w-50.25 cursor-pointer transition-colors duration-200",
  {
    variants: {
      color: {
        default: " hover:bg-primary/20 active:bg-primary/30",
        premium: "hover:text-premium-dark hover:bg-premium/20 active:bg-premium/30",
      },
      selected: { true: "", false: "bg-transparent" },
    },
    compoundVariants: [
      {
        color: "default",
        selected: true,
        class: "bg-primary/20",
      },
      {
        color: "premium",
        selected: true,
        class: "bg-premium/20 text-premium-dark",
      },
    ],
    defaultVariants: {
      color: "default",
      selected: false,
    },
  },
);

const TabButton = forwardRef<HTMLButtonElement, TabButtonPropsWithoutRef>((props, ref) => {
  const { className, color, selected, ...rest } = props;

  return (
    <button ref={ref} className={cn(tabButtonVariants({ color, selected }), className)} {...rest} />
  );
});

interface FilterTab<KeyType extends string = string, T = SimplePrimitive>
  extends LabelValuePair<KeyType, T> {
  label: KeyType;
  value: T;
  color?: TabButtonPropsWithoutRef["color"];
}

interface FilterTabsProps<KeyType extends string, T extends Maybe<Nullable<SimplePrimitive>>>
  extends Omit<ComponentProps<"div">, "onChange"> {
  tabs: FilterTab<KeyType, T>[];
  value: T;
  onChange: (value: T) => void;
}

const FilterTabs = <KeyType extends string, T extends Maybe<Nullable<SimplePrimitive>>>({
  tabs,
  value,
  onChange,
  className,
  ...props
}: FilterTabsProps<KeyType, T>) => {
  return (
    <div
      className={cn(
        "max-width-full bg-paper flex w-max flex-wrap justify-center gap-1 rounded-md p-1",
        className,
      )}
      {...props}
    >
      {tabs.map((tab, index) => (
        <TabButton
          key={index}
          color={tab.color}
          selected={tab.value === value}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </TabButton>
      ))}
    </div>
  );
};

export { FilterTabs, type FilterTabsProps, type FilterTab };
